import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBrain } from '../lib/brain';
import { useTacticsCommitForBoard } from '../lib/brainTactics';
import { loadTacticsLayout } from '../lib/tactics3dStore';
import { useVoiceToText } from '../lib/voiceInput';
import { useBrainAutoSpeak } from '../lib/useBrainAutoSpeak';
import { isPlaceholderSupabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { BrainPrompt } from './primitives/BrainPrompt';
import { DraftCard } from './DraftCard';
import { HudChip } from './tacticsHud';
import type { BrainTurn } from '../lib/brain';
import './TacticsBrainPanel.css';
// HudChip lives in the tactics HUD; pull in its stylesheet explicitly so the
// `.thud__chip` rules are present even when this dock renders outside a
// HudShell (tests, smoke routes, etc).
import './tacticsHud/tacticsHud.css';

/**
 * How many turns the scrollable history keeps in view at once. The
 * Brain hook itself keeps the full conversation; this constant only
 * caps the rendered window so the dock doesn't grow unbounded on
 * long iteration sessions. Bump it if coaches start asking for more.
 */
const MAX_HISTORY_TURNS = 6;

interface TacticsBrainPanelProps {
  /** localStorage key for the tactics board this panel drives. */
  persistKey: string;
  /** Which surface — feeds the Brain's screen context. */
  surface: '2d' | '3d';
  /** Active team for memory + draft scoping. Optional. */
  teamId?: string | null;
}

type BrainBackendState = 'offline-stub' | 'live' | 'unauthenticated';

/**
 * Resolves which Brain backend the panel is talking to right now.
 * Coaches running offline need to know the canned demo responses are
 * scripted — not real Brain reasoning — so this badge surfaces the
 * three observable states without a fancy detection lib.
 */
function resolveBrainBackendState(
  placeholderSupabase: boolean,
  hasUser: boolean,
): BrainBackendState {
  if (placeholderSupabase) return 'offline-stub';
  if (!hasUser) return 'unauthenticated';
  return 'live';
}

const BRAIN_BACKEND_COPY: Record<BrainBackendState, { label: string; tooltip: string }> = {
  'offline-stub': {
    label: 'offline · canned demo responses',
    tooltip:
      'No Supabase backend configured — you are seeing scripted offline output, not real Brain reasoning. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to connect to the real Brain.',
  },
  live: {
    label: 'live (claude-sonnet-4)',
    tooltip:
      'Connected to the real Brain via the Supabase proxy. Responses are generated by Claude Sonnet 4 with the active team and tactics context.',
  },
  unauthenticated: {
    label: "signed out · history won't persist",
    tooltip:
      "You're signed out. Tactics drafts still work locally on this device, but conversation history requires sign-in to persist.",
  },
};

function BrainBackendBadge({ state }: { state: BrainBackendState }) {
  const { label, tooltip } = BRAIN_BACKEND_COPY[state];
  return (
    <div
      className="tactics-brain-panel__backend-badge"
      data-state={state}
      data-testid="brain-backend-badge"
      role="status"
      title={tooltip}
      aria-label={`Brain backend: ${label}`}
    >
      <span className="tactics-brain-panel__backend-badge__dot" aria-hidden="true" />
      <span className="tactics-brain-panel__backend-badge__label">{label}</span>
    </div>
  );
}

/**
 * Inline Brain interface for the tactics-board pages. Renders a scrollable
 * history of recent turns, a row of flow buttons (Clear / Re-ask / Copy
 * intent), the BrainPrompt input, and the backend badge.
 *
 * History rendering — newest at the bottom, auto-scrolls on each new turn.
 * "Clear" is implemented locally via a `historyStart` index (no edits to
 * the Brain hook): bumping it past the current turn count hides everything
 * already in the buffer while keeping future turns visible. On `send`, the
 * panel resets `historyStart` to 0 so the new turn shows.
 *
 * Why inline and not the global Brain Bar (⌘K)? The tactics routes
 * (`/sessions/:id/3d` and `/2d`) are full-bleed — they live outside
 * AppShell where the global BrainChatPanel is mounted. Phase 1
 * ships this dedicated surface; a later phase will lift the global
 * Brain Bar above AppShell so the ⌘K shortcut also covers
 * full-bleed routes (per AGENTS.md's "Brain Bar is the only
 * command surface" directive).
 */
export function TacticsBrainPanel({
  persistKey,
  surface,
  teamId = null,
}: TacticsBrainPanelProps) {
  // Read the layout on demand — the dispatcher calls this each turn
  // so it reflects the current state, not a stale closure capture.
  const getTacticsLayout = useCallback(() => loadTacticsLayout(persistKey), [
    persistKey,
  ]);

  const { commit, commitBriefs } = useTacticsCommitForBoard(persistKey);

  const screen = useMemo(
    () =>
      ({
        kind: 'tactics' as const,
        tacticsPersistKey: persistKey,
        tacticsSurface: surface,
      }) as const,
    [persistKey, surface],
  );

  const brain = useBrain({
    teamId,
    screen,
    getTacticsLayout,
    onCommitTacticsDraft: commit,
    onCommitPlayerBriefsDraft: commitBriefs,
  });

  const voice = useVoiceToText({
    onTranscript: (text) => brain.send(text),
  });
  useBrainAutoSpeak({ turns: brain.turns, loading: brain.loading });

  const { user } = useAuth();
  const backendState = useMemo(
    () => resolveBrainBackendState(isPlaceholderSupabase(), Boolean(user)),
    [user],
  );

  // Local "clear" implementation: index into brain.turns below which we
  // hide turns from the rendered list. Bumping it to brain.turns.length
  // effectively wipes the visible history without mutating the Brain
  // hook's internal state. A new `send` resets this to 0 so the next
  // turn shows up immediately.
  const [historyStart, setHistoryStart] = useState(0);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'no-intent' | 'failed'>(
    'idle',
  );

  const visibleTurns: BrainTurn[] = useMemo(() => {
    const sliced = brain.turns.slice(historyStart);
    // Cap to the most-recent MAX_HISTORY_TURNS so an iteration-heavy
    // session doesn't grow the dock unbounded.
    return sliced.slice(-MAX_HISTORY_TURNS);
  }, [brain.turns, historyStart]);

  const hasVisibleHistory = visibleTurns.length > 0;

  // Latest user turn — used by Re-ask to refire the most recent prompt.
  const latestUserText = useMemo(() => {
    for (let i = brain.turns.length - 1; i >= 0; i -= 1) {
      const turn = brain.turns[i];
      if (turn.role === 'user' && turn.text.trim().length > 0) return turn.text;
    }
    return null;
  }, [brain.turns]);

  // Latest *pending* draft — Copy intent reads its `intent` field if present.
  const latestPendingDraft = useMemo(() => {
    for (let i = brain.turns.length - 1; i >= 0; i -= 1) {
      const turn = brain.turns[i];
      if (turn.draft && turn.status === 'pending') return turn.draft;
    }
    return null;
  }, [brain.turns]);

  // Auto-scroll the history list to the bottom whenever a new turn lands
  // or the loading status flips. Using a sentinel `<li>` at the end +
  // scrollIntoView keeps this simple and avoids measuring scrollHeight.
  const endRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!endRef.current) return;
    endRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [visibleTurns.length, brain.loading]);

  const handleSend = useCallback(
    (text: string) => {
      // Reset the clear-window so the new turn is always visible.
      setHistoryStart(0);
      setCopyStatus('idle');
      brain.send(text);
    },
    [brain],
  );

  const handleClear = useCallback(() => {
    setHistoryStart(brain.turns.length);
    setCopyStatus('idle');
  }, [brain.turns.length]);

  const handleReask = useCallback(() => {
    if (!latestUserText) return;
    handleSend(latestUserText);
  }, [latestUserText, handleSend]);

  const handleCopyIntent = useCallback(() => {
    const payload = latestPendingDraft?.payload;
    const intent =
      payload && typeof payload === 'object' && payload !== null && 'intent' in payload
        ? (payload as { intent?: unknown }).intent
        : undefined;
    if (typeof intent !== 'string' || intent.trim().length === 0) {
      setCopyStatus('no-intent');
      return;
    }
    // navigator.clipboard.writeText is async but we don't await — the
    // pill flips optimistically. If the platform rejects the write
    // (Safari private mode, etc.) the catch arm flips it to failed.
    try {
      const result = navigator.clipboard?.writeText(intent);
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>)
          .then(() => setCopyStatus('copied'))
          .catch(() => setCopyStatus('failed'));
      } else {
        setCopyStatus('copied');
      }
    } catch {
      setCopyStatus('failed');
    }
  }, [latestPendingDraft]);

  const copyPillCopy: Record<typeof copyStatus, string | null> = {
    idle: null,
    copied: 'intent copied to clipboard',
    'no-intent': 'no intent on latest draft',
    failed: 'copy failed',
  };
  const copyPill = copyPillCopy[copyStatus];

  return (
    <div className="tactics-brain-panel" data-testid="tactics-brain-panel">
      <header className="tactics-brain-panel__header">
        <span className="tactics-brain-panel__eyebrow">Brain · designer</span>
        <h2 className="tactics-brain-panel__title">Ask, draw, refine.</h2>
      </header>

      {!hasVisibleHistory ? (
        <TacticsBrainExamples surface={surface} onPick={(text) => handleSend(text)} />
      ) : (
        <ul
          className="tactics-brain-panel__history"
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="Brain conversation history"
          data-testid="brain-history"
        >
          {visibleTurns.map((turn) => (
            <TacticsBrainTurnCard
              key={turn.id}
              turn={turn}
              onApprove={() => brain.approveDraft(turn.id)}
              onDismiss={() => brain.dismissDraft(turn.id)}
            />
          ))}
          <li
            ref={endRef}
            aria-hidden="true"
            className="tactics-brain-panel__history-end"
            data-testid="brain-history-end"
          />
        </ul>
      )}

      {brain.loading && (
        <p className="tactics-brain-panel__status">{brain.status || 'thinking…'}</p>
      )}

      <div
        className="tactics-brain-panel__flow"
        role="toolbar"
        aria-label="Brain flow controls"
      >
        <HudChip
          onClick={handleClear}
          ariaLabel="Clear conversation"
        >
          Clear
        </HudChip>
        <HudChip
          onClick={handleReask}
          ariaLabel="Re-ask last prompt"
        >
          Re-ask
        </HudChip>
        <HudChip
          onClick={handleCopyIntent}
          ariaLabel="Copy draft intent to clipboard"
        >
          Copy intent
        </HudChip>
        {copyPill && (
          <span
            className="tactics-brain-panel__flow-pill"
            data-testid="brain-flow-pill"
            role="status"
          >
            {copyPill}
          </span>
        )}
      </div>

      <BrainPrompt
        placeholder={
          surface === '3d'
            ? "say or type — e.g. 'high press from the right back'"
            : "say or type — e.g. 'compact 5-3-2 low block vs 4-4-2'"
        }
        hint="brain ready — voice or text"
        ambient={false}
        listening={voice.listening}
        voiceStatus={voice.status}
        onSubmit={(text) => handleSend(text)}
        onMicStart={voice.start}
        onMicStop={voice.stop}
      />
      <BrainBackendBadge state={backendState} />
    </div>
  );
}

interface TacticsBrainTurnCardProps {
  turn: BrainTurn;
  onApprove: () => void;
  onDismiss: () => void;
}

/**
 * One row in the scrollable history. User turns right-align with a
 * faint accent border; assistant turns left-align with no border.
 * Pending drafts render the full DraftCard so the coach can approve
 * inline; settled drafts collapse to a one-line status note.
 */
function TacticsBrainTurnCard({ turn, onApprove, onDismiss }: TacticsBrainTurnCardProps) {
  const isUser = turn.role === 'user';
  const hasPendingDraft = Boolean(turn.draft) && turn.status === 'pending';
  const hasSettledDraft = Boolean(turn.draft) && turn.status && turn.status !== 'pending';

  return (
    <li
      className={`tactics-brain-panel__turn tactics-brain-panel__turn--${isUser ? 'user' : 'assistant'}`}
      data-testid={`brain-turn-${turn.id}`}
      data-role={turn.role}
    >
      <span className="tactics-brain-panel__turn-role">
        {isUser ? 'Coach' : 'Brain'}
      </span>
      {turn.text && !hasPendingDraft && (
        <p className="tactics-brain-panel__turn-text">{turn.text}</p>
      )}
      {hasPendingDraft && turn.draft && (
        <DraftCard
          type={turn.draft.type}
          payload={turn.draft.payload}
          status={turn.status}
          onApprove={onApprove}
          onDismiss={onDismiss}
        />
      )}
      {hasSettledDraft && (
        <p
          className="tactics-brain-panel__turn-draft-settled"
          data-status={turn.status}
        >
          {turn.status === 'approved' ? '✓ saved' : '× dismissed'}
        </p>
      )}
    </li>
  );
}

const EXAMPLES_2D = [
  '4-3-3 high press from the right back',
  'compact low block vs 4-4-2',
  'switch to a back three',
  'critique the current shape',
] as const;
const EXAMPLES_3D = [
  '4-3-3 high press, RB triggers',
  'mid-block + counter-press trigger',
  '3-4-2-1 with inverted full-backs',
  'critique the current shape',
] as const;

interface TacticsBrainExamplesProps {
  surface: '2d' | '3d';
  onPick: (text: string) => void;
}

function TacticsBrainExamples({ surface, onPick }: TacticsBrainExamplesProps) {
  const examples = surface === '3d' ? EXAMPLES_3D : EXAMPLES_2D;
  return (
    <section
      className="tactics-brain-panel__examples"
      aria-label="Example prompts"
    >
      <span className="tactics-brain-panel__examples__label">Try one</span>
      {examples.map((text) => (
        <button
          key={text}
          type="button"
          className="tactics-brain-panel__example-chip"
          onClick={() => onPick(text)}
        >
          {text}
        </button>
      ))}
    </section>
  );
}
